import { useState } from 'react';

interface GeocodingResult {
  lat: number;
  lon: number;
  display_name: string;
}

export function useGeocoding() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCoordinatesFromAddress = async (address: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to geocode address');
      }

      const results: GeocodingResult[] = await response.json();
      
      if (results.length === 0) {
        throw new Error('Address not found');
      }

      const { lon, lat } = results[0];
      
      return {
        type: 'Point' as const,
        coordinates: [Number(lon), Number(lat)] as [number, number],
        display_name: results[0].display_name
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to geocode address');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const reverseGeocode = async (coordinates: [number, number]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [lon, lat] = coordinates;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to reverse geocode coordinates');
      }

      const result = await response.json();
      
      if (!result.address) {
        throw new Error('Location not found');
      }

      return {
        address: {
          street: result.address.road || '',
          city: result.address.city || result.address.town || '',
          state: result.address.state || '',
          country: result.address.country || '',
          postalCode: result.address.postcode || ''
        },
        display_name: result.display_name
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reverse geocode');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    getCoordinatesFromAddress, 
    reverseGeocode,
    isLoading, 
    error 
  };
}